import { useState, useEffect } from 'react';
import { User, Session } from '@supabase/supabase-js';
import { supabase } from '@/integrations/supabase/client';
import { toast } from '@/hooks/use-toast';

export const useAuth = () => {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    console.log("useAuth: Setting up auth state listener");
    let isMounted = true;
    
    // Set up auth state listener FIRST
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (event, session) => {
        if (!isMounted) return;
        
        // Check if password reset is in progress - don't process auth changes that could cause redirects
        const isPasswordResetInProgress = sessionStorage.getItem('password_reset_in_progress') === 'true';
        if (isPasswordResetInProgress) {
          console.log("useAuth: Password reset in progress, skipping auth state processing");
          // Still update session for password reset to work, but don't trigger loading changes
          setSession(session);
          setUser(session?.user ?? null);
          return;
        }
        
        console.log("useAuth: Auth state changed:", event);
        setSession(session);
        setUser(session?.user ?? null);
        setLoading(false);
      }
    );

    // THEN check for existing session
    supabase.auth.getSession().then(({ data: { session }, error }) => {
      if (!isMounted) return;
      
      if (error) {
        console.error("useAuth: Error getting session:", error);
      } else {
        console.log("useAuth: Initial session check complete");
      }
      setSession(session);
      setUser(session?.user ?? null);
      setLoading(false);
    });

    return () => {
      console.log("useAuth: Cleaning up auth subscription");
      isMounted = false;
      subscription.unsubscribe();
    };
  }, []);

  const signOut = async () => {
    // Clear local state first to ensure UI updates immediately
    setUser(null);
    setSession(null);
    
    // Then attempt server-side signOut (may fail if session already expired)
    try {
      await supabase.auth.signOut();
    } catch (error) {
      // Session may already be invalid on server - that's okay since we cleared local state
      console.log('Sign out completed (session may have been expired)');
    }
    
    toast({
      title: "Signed out",
      description: "You have been successfully signed out.",
    });
  };

  return {
    user,
    session,
    loading,
    signOut,
    isAuthenticated: !!user,
  };
};